import { useCallback, useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import type mapboxgl from 'mapbox-gl'
import { Marker } from 'react-map-gl/mapbox'
import {
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_HOTEL,
} from 'constants/offer'
import config from 'constants/config'
import PropertyMarker from 'components/Common/PropertyMap/PropertyMarkerV2'
import { getPropertyMarkerMapboxOffset } from 'components/Common/PropertyMap/PropertyMarkerV2/pinsV2'
import PropertyPriceMarker from './PropertyPriceMarker'
import Clickable from 'components/Common/Clickable'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import {
  getOfferId,
} from 'components/Search/SearchResultsMap/utils'
import { autoPlacement, autoUpdate, offset, shift, useFloating } from '@floating-ui/react-dom'
import SearchResultsMapOfferView from 'components/Search/SearchResultsMap/SearchResultsMapOfferView'

const MAX_PIN_LIMIT = config.search.mapView.pinLimit
const MAP_TILE_EDGE_GAP = 16
const MAP_TILE_TOP_OFFSET = 64
const MAP_TILE_SIDE_OFFSET = 28

interface Props {
  onMarkerClick: (
    marker: App.SearchMapMarker,
    pinType: string,
    e?: Event | React.MouseEvent<Element | MouseEvent>,
  ) => void
  isActive: boolean
  isHovered: boolean
  isVisited: boolean
  filters: App.OfferListFilters
  onDeselectOffers?: () => void
  marker: App.SearchMapMarker
  index: number
  handleProductClick?: (
    offer:
      | App.Offer
      | App.OfferSummary
      | App.BedbankOffer
      | App.BedbankOfferSummary,
    position: number,
  ) => void
  onHover?: (offerId: string | undefined, type: string | undefined) => void
  soldOut: boolean
  isSaved?: boolean
  map?: mapboxgl.Map | null
  animateMarker?: boolean
  showOfferViewWhenActive?: boolean
  showPerNightPricing?: boolean
  priceLabel?: string
  isLuxPlusPrice?: boolean
}

function HotelMapMarker(props: Props) {
  const {
    marker,
    index,
    onMarkerClick,
    isActive,
    isVisited,
    filters,
    onDeselectOffers,
    handleProductClick,
    isHovered,
    onHover,
    soldOut,
    isSaved,
    map,
    animateMarker = false,
    showOfferViewWhenActive = true,
    showPerNightPricing = false,
    priceLabel,
    isLuxPlusPrice = false,
  } = props

  const calculateZIndex = useCallback(
    (marker: App.SearchMapMarker, index: number) => {
      // Keep hovered/active strictly above ranked pins (`MAX_PIN_LIMIT - index`) and the landmark pin.
      if (isActive) {
        return MAX_PIN_LIMIT + 2
      }
      if (isHovered) {
        return MAX_PIN_LIMIT + 1
      }
      if (soldOut) {
        return -1
      }
      if (index >= MAX_PIN_LIMIT) {
        return 0
      }
      const offerType = marker.offers[0].type
      if (
        offerType === OFFER_TYPE_HOTEL ||
        offerType === OFFER_TYPE_ALWAYS_ON ||
        offerType === OFFER_TYPE_BED_BANK
      ) {
        return MAX_PIN_LIMIT - index
      }
      return 0
    },
    [isActive, isHovered, soldOut],
  )

  const handleClick = useCallback(
    (e: Event | React.MouseEvent<Element | MouseEvent>) => {
      onMarkerClick(marker, index < MAX_PIN_LIMIT ? 'pricePin' : 'pill', e)
      e.stopPropagation()
    },
    [index, marker, onMarkerClick],
  )

  const handleMouseEnter = useCallback(() => {
    if (onHover) {
      onHover(marker.id, marker.offers[0].type)
    }
  }, [marker.id, marker.offers, onHover])

  const handleMouseLeave = useCallback(() => {
    if (onHover) {
      onHover(undefined, undefined)
    }
  }, [onHover])

  const offerIds = useMemo(() => marker.offers.map(getOfferId), [marker.offers])

  const zIndex = useMemo(() => calculateZIndex(marker, index), [calculateZIndex, index, marker])
  const markerOffset = useMemo(
    () => (priceLabel ? undefined : getPropertyMarkerMapboxOffset(marker, {
      active: isActive,
      visited: isVisited,
      soldOut,
      isHovered,
    })),
    [isActive, isHovered, isVisited, marker, priceLabel, soldOut],
  )

  const { refs, floatingStyles, update } = useFloating({
    middleware: [
      offset(({ placement }) => {
        // Reference point is the marker anchor (pin tip), so top placement
        // needs extra offset to clear the visible pin body.
        if (placement.includes('top')) return MAP_TILE_TOP_OFFSET
        if (placement.includes('bottom')) return MAP_TILE_EDGE_GAP
        return MAP_TILE_SIDE_OFFSET
      }),
      shift({ padding: MAP_TILE_EDGE_GAP }),
      autoPlacement({ padding: MAP_TILE_EDGE_GAP }),
    ],
    open: isActive,
    whileElementsMounted: autoUpdate,
  })
  const floatingElementRef = useRef<HTMLDivElement | null>(null)
  const shouldRestoreDoubleClickZoomRef = useRef(false)

  const stopOfferCardDoubleClickPropagation = useCallback((event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  const setFloatingElement = useCallback((node: HTMLDivElement | null) => {
    if (floatingElementRef.current) {
      floatingElementRef.current.removeEventListener('dblclick', stopOfferCardDoubleClickPropagation)
    }

    floatingElementRef.current = node
    refs.setFloating(node)

    if (node) {
      node.addEventListener('dblclick', stopOfferCardDoubleClickPropagation)
    }
  }, [refs, stopOfferCardDoubleClickPropagation])

  useEffect(() => {
    if (!map || !isActive) {
      return
    }

    map.on('move', update)

    return () => {
      map.off('move', update)
    }
  }, [isActive, map, update])

  const disableMapDoubleClickZoom = useCallback(() => {
    if (!map) {
      return
    }

    const isEnabled = map.doubleClickZoom.isEnabled?.() ?? true
    shouldRestoreDoubleClickZoomRef.current = isEnabled

    if (isEnabled) {
      map.doubleClickZoom.disable()
    }
  }, [map])

  const restoreMapDoubleClickZoom = useCallback(() => {
    if (!map || !shouldRestoreDoubleClickZoomRef.current) {
      return
    }

    map.doubleClickZoom.enable()
    shouldRestoreDoubleClickZoomRef.current = false
  }, [map])

  useEffect(() => {
    if (!isActive) {
      restoreMapDoubleClickZoom()
    }
  }, [isActive, restoreMapDoubleClickZoom])

  useEffect(() => () => {
    restoreMapDoubleClickZoom()

    if (floatingElementRef.current) {
      floatingElementRef.current.removeEventListener('dblclick', stopOfferCardDoubleClickPropagation)
    }
  }, [restoreMapDoubleClickZoom, stopOfferCardDoubleClickPropagation])

  return (
    <Marker
      longitude={marker.longitude ?? 0}
      latitude={marker.latitude ?? 0}
      anchor="bottom"
      offset={markerOffset}
      style={{ zIndex }}
    >
      <div style={{ position: 'relative' }}>
        <Clickable
          ref={refs.setReference}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {priceLabel && <PropertyPriceMarker
            isActive={isActive}
            isHovered={isHovered}
            isVisited={isVisited}
            isLuxPlusPrice={isLuxPlusPrice}
            label={priceLabel}
          />}
          {!priceLabel && <PropertyMarker
            key={marker.id}
            marker={marker}
            active={isActive}
            isHovered={isHovered}
            visited={isVisited}
            soldOut={soldOut}
            isSaved={isSaved}
            animateMarker={animateMarker}
          />}
        </Clickable>
        {showOfferViewWhenActive && (
          <CSSBreakpoint min="tablet">
            <div
              ref={setFloatingElement}
              style={floatingStyles}
              data-testid="hotel-map-marker-offer-card"
              onMouseEnter={disableMapDoubleClickZoom}
              onMouseLeave={restoreMapDoubleClickZoom}
            >
              {isActive && (
                <SearchResultsMapOfferView
                  key={marker.id}
                  offerIds={offerIds}
                  filters={filters}
                  showPerNightPricing={showPerNightPricing}
                  onProductClick={handleProductClick}
                  onDeselectOffers={onDeselectOffers}
                />
              )}
            </div>
          </CSSBreakpoint>
        )}
      </div>
    </Marker>
  )
}

export default React.memo(HotelMapMarker)
